<template>
  <div class="analytics-dashboard animated fadeIn">
    <!-- ─── HEADER ─── -->
    <div class="report-header-premium mb-4 rounded shadow-sm d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 p-md-4">
      <div class="report-header-premium__info d-flex align-items-center mb-3 mb-md-0">
        <div class="report-header-premium__icon d-none d-sm-flex">
          <i class="fa fa-line-chart text-white"></i>
        </div>
        <div class="ml-sm-3">
          <h4 class="report-header-premium__title text-white mb-1">CECFrigo Analytics</h4>
          <div class="d-flex align-items-center flex-wrap">
            <span class="report-header-premium__sub text-white-50">Painel Executivo de Lucratividade</span>
            <b-badge variant="success" pill class="ml-2 py-1 px-2 border border-white-50 shadow-sm" style="font-size: 0.65rem;">
              <i class="fa fa-trophy mr-1"></i> PREMIUM
            </b-badge>
          </div>
        </div>
      </div>
      
      <div class="d-flex align-items-center flex-wrap gap-2">
        <b-button variant="outline-light" size="sm" class="mr-3 btn-imprimir" @click="imprimir" v-b-tooltip.hover title="Imprimir ou Salvar em PDF">
          <i class="fa fa-print"></i>
        </b-button>
        
        <div class="filter-pills d-flex align-items-center bg-white-10 rounded-pill px-3 py-2">
          <b-form-select 
            v-model="filtro.mes" 
            :options="opcoesMeses" 
            size="sm" 
            class="bg-transparent border-0 text-white font-weight-bold p-0 mr-2 select-transparent" 
            style="width: 120px; cursor: pointer;"
            @change="buscarDados"
          ></b-form-select>
          <div class="v-divider mx-2"></div>
          <b-form-select 
            v-model="filtro.ano" 
            :options="opcoesAnos" 
            size="sm" 
            class="bg-transparent border-0 text-white font-weight-bold p-0 select-transparent" 
            style="width: 70px; cursor: pointer;"
            @change="buscarDados"
          ></b-form-select>
        </div>
      </div>
    </div>

    <!-- Overlay de Carregamento -->
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="primary" label="Carregando..."></b-spinner>
      <p class="mt-2 text-muted">Processando cubo de dados...</p>
    </div>

    <template v-else>
      <!-- KPIs Rápidos -->
      <b-row class="mb-4">
        <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
          <div class="kpi-card-premium border-left-primary h-100">
            <small>Faturamento Bruto</small>
            <div class="d-flex align-items-end justify-content-between">
              <h3 class="mb-0 text-primary">{{ formatBRL(totais.bruto) }}</h3>
              <div class="kpi-icon-bg bg-primary-light d-none d-lg-flex"><i class="fa fa-shopping-cart"></i></div>
            </div>
          </div>
        </b-col>
        <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
          <div class="kpi-card-premium border-left-danger h-100">
            <small>Custo Total (CMV)</small>
            <div class="d-flex align-items-end justify-content-between">
              <h3 class="mb-0 text-danger">{{ formatBRL(totais.custo) }}</h3>
              <div class="kpi-icon-bg bg-danger-light d-none d-lg-flex"><i class="fa fa-tags"></i></div>
            </div>
          </div>
        </b-col>
        <b-col sm="6" lg="3" class="mb-3 mb-sm-0">
          <div class="kpi-card-premium border-left-success h-100 shadow-success-sm">
            <small>Lucro Bruto Gerencial</small>
            <div class="d-flex align-items-end justify-content-between">
              <h3 class="mb-0 text-success">{{ formatBRL(totais.lucro) }}</h3>
              <div class="kpi-icon-bg bg-success-light d-none d-lg-flex"><i class="fa fa-money"></i></div>
            </div>
          </div>
        </b-col>
        <b-col sm="6" lg="3">
          <div class="kpi-card-premium border-left-info h-100">
            <small>Margem Bruta Média</small>
            <div class="d-flex align-items-end justify-content-between">
              <h3 class="mb-0 text-info">{{ margemMedia }}%</h3>
              <div class="kpi-icon-bg bg-info-light d-none d-lg-flex"><i class="fa fa-percent"></i></div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Gráficos Principais -->
      <b-row class="mb-4">
        <b-col md="12">
          <b-card class="border-0 shadow-sm chart-card" :bg-variant="isDark ? 'gray-800' : 'white'">
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <i class="fa fa-area-chart mr-2 text-primary"></i>
                  <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">Evolução do Lucro no Período</span>
                </div>
              </div>
            </template>
            <div class="chart-container" style="position: relative; height: 300px;">
              <canvas ref="chartEvolucao"></canvas>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mb-4">
        <b-col lg="6" class="mb-4 mb-lg-0">
          <b-card class="border-0 shadow-sm chart-card h-100" :bg-variant="isDark ? 'gray-800' : 'white'">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fa fa-pie-chart mr-2 text-success"></i>
                <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">Participação no Lucro por Grupo</span>
              </div>
            </template>
            <div class="chart-container" style="position: relative; height: 280px;">
              <canvas ref="chartGrupos"></canvas>
            </div>
          </b-card>
        </b-col>

        <b-col lg="6">
          <b-card class="border-0 shadow-sm chart-card h-100" :bg-variant="isDark ? 'gray-800' : 'white'">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fa fa-cubes mr-2 text-info"></i>
                <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">Top 10 Produtos (Maior Margem Absoluta)</span>
              </div>
            </template>
            <div class="chart-container" style="position: relative; height: 280px;">
              <canvas ref="chartProdutos"></canvas>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Tabela Analítica -->
      <b-row>
        <b-col md="12">
          <b-card class="border-0 shadow-sm" :bg-variant="isDark ? 'gray-800' : 'white'">
            <template #header>
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-2 mb-md-0">
                  <i class="fa fa-list-ul mr-2 text-primary"></i>
                  <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">Auditoria de Rentabilidade por Produto</span>
                </div>
                <b-input-group size="sm" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <i class="fa fa-search"></i>
                  </b-input-group-prepend>
                  <b-form-input v-model="searchTerm" placeholder="Buscar produto ou grupo..."></b-form-input>
                </b-input-group>
              </div>
            </template>
            <b-table
              :items="dadosFiltrados"
              :fields="fields"
              hover
              striped
              responsive
              class="mb-0"
              head-variant="light"
              :table-class="isDark ? 'table-dark' : ''"
              show-empty
              empty-text="Nenhum dado encontrado para o filtro atual."
              :per-page="perPage"
              :current-page="currentPage"
            >
              <template #cell(DT_SAIDA)="data">
                {{ formatDataBR(data.value) }}
              </template>
              <template #cell(VL_BRUTO)="data">
                {{ formatBRL(data.value) }}
              </template>
              <template #cell(VL_CUSTO)="data">
                <span class="text-danger">{{ formatBRL(data.value) }}</span>
              </template>
              <template #cell(VL_LUCRO)="data">
                <span class="text-success font-weight-bold">{{ formatBRL(data.value) }}</span>
              </template>
              <template #cell(MARGEM)="data">
                <div class="d-flex align-items-center justify-content-center">
                  <span class="font-weight-bold mr-2" :style="{ color: getMargemHex(data.item) }">
                    {{ calculateMargem(data.item) }}%
                  </span>
                  <div class="progress d-none d-sm-flex" style="height: 6px; width: 60px;">
                    <div class="progress-bar" :style="{ width: calculateMargem(data.item) + '%', backgroundColor: getMargemHex(data.item) }"></div>
                  </div>
                </div>
              </template>
            </b-table>
            
            <div class="mt-3 d-flex justify-content-end" v-if="dadosFiltrados.length > perPage">
              <b-pagination
                v-model="currentPage"
                :total-rows="dadosFiltrados.length"
                :per-page="perPage"
                size="sm"
              ></b-pagination>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import 'moment/locale/pt-br'

export default {
  name: 'AnalyticsDashboard',
  data() {
    return {
      loading: true,
      dadosLocal: [],
      searchTerm: '',
      currentPage: 1,
      perPage: 15,
      filtro: {
        mes: moment().month() + 1,
        ano: moment().year()
      },
      charts: {
        evolucao: null,
        grupos: null,
        produtos: null
      },
      opcoesMeses: [
        { value: 1, text: 'Janeiro' }, { value: 2, text: 'Fevereiro' },
        { value: 3, text: 'Março' }, { value: 4, text: 'Abril' },
        { value: 5, text: 'Maio' }, { value: 6, text: 'Junho' },
        { value: 7, text: 'Julho' }, { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setembro' }, { value: 10, text: 'Outubro' },
        { value: 11, text: 'Novembro' }, { value: 12, text: 'Dezembro' }
      ],
      fields: [
        { key: 'DT_SAIDA', label: 'Data', sortable: true },
        { key: 'PRODUTO', label: 'Produto', sortable: true },
        { key: 'GRUPO', label: 'Grupo', sortable: true },
        { key: 'VL_BRUTO', label: 'Fat. Bruto', class: 'text-right', sortable: true },
        { key: 'VL_CUSTO', label: 'CMV (Custo)', class: 'text-right', sortable: true },
        { key: 'VL_LUCRO', label: 'Lucro Bruto', class: 'text-right', sortable: true },
        { key: 'MARGEM', label: 'Margem %', class: 'text-center', sortable: true }
      ]
    }
  },
  computed: {
    isDark() { return this.$store.getters['tema/IS_DARK'] },
    opcoesAnos() {
      const anos = []
      const anoAtual = moment().year()
      for (let i = 0; i < 5; i++) {
        anos.push({ value: anoAtual - i, text: (anoAtual - i).toString() })
      }
      return anos
    },
    totais() {
      const bruto = this.dadosLocal.reduce((acc, it) => acc + (it.VL_BRUTO || 0), 0)
      const custo = this.dadosLocal.reduce((acc, it) => acc + (it.VL_CUSTO || 0), 0)
      const lucro = this.dadosLocal.reduce((acc, it) => acc + (it.VL_LUCRO || 0), 0)
      return { bruto, custo, lucro }
    },
    margemMedia() {
      if (this.totais.bruto === 0) return 0
      return Math.round((this.totais.lucro / this.totais.bruto) * 100)
    },
    dadosFiltrados() {
      if (!this.searchTerm) return this.dadosLocal
      const term = this.searchTerm.toLowerCase()
      return this.dadosLocal.filter(it => 
        (it.PRODUTO || '').toLowerCase().includes(term) ||
        (it.GRUPO || '').toLowerCase().includes(term)
      )
    }
  },
  watch: {
    isDark() {
      this.renderCharts()
    }
  },
  methods: {
    formatBRL(v) {
      return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatDataBR(v) {
      if (!v) return '-'
      return moment(v).format('DD/MM/YYYY')
    },
    calculateMargem(item) {
      if (!item.VL_BRUTO || item.VL_BRUTO === 0) return 0
      return Math.round((item.VL_LUCRO / item.VL_BRUTO) * 100)
    },
    getMargemHex(item) {
      const m = (item.VL_LUCRO / item.VL_BRUTO) * 100
      if (m >= 25) return '#10b981' // Success
      if (m >= 15) return '#3b82f6' // Info
      if (m >= 5) return '#f59e0b'  // Warning
      return '#ef4444' // Danger
    },
    imprimir() {
      setTimeout(() => {
        window.print()
      }, 300)
    },
    async buscarDados() {
      this.loading = true
      try {
        const response = await this.$http.get('api/protected/venda/detalharLucroMensal', {
          params: { mes: this.filtro.mes, ano: this.filtro.ano }
        })
        this.dadosLocal = response.data || []
        this.loading = false
        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (error) {
        console.error('Erro ao buscar dados de lucro:', error)
        this.$bvToast.toast('Erro ao carregar dados do Analytics', { variant: 'danger' })
        this.loading = false
      }
    },
    renderCharts() {
      if (this.loading) return
      setTimeout(() => {
        this.renderEvolucao()
        this.renderGrupos()
        this.renderProdutos()
      }, 100)
    },
    getChartFontColor() {
      return this.isDark ? '#cbd5e1' : '#64748b'
    },
    getChartGridColor() {
      return this.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
    },
    renderEvolucao() {
      if (!this.$refs.chartEvolucao) return
      const ctx = this.$refs.chartEvolucao.getContext('2d')
      if (this.charts.evolucao) this.charts.evolucao.destroy()
      
      const diasMap = {}
      this.dadosLocal.forEach(it => {
        const dia = moment(it.DT_SAIDA).format('DD/MM')
        if (!diasMap[dia]) diasMap[dia] = 0
        diasMap[dia] += (it.VL_LUCRO || 0)
      })
      const labels = Object.keys(diasMap).sort()
      const values = labels.map(l => diasMap[l])
      
      const gradient = ctx.createLinearGradient(0, 0, 0, 300)
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)')
      gradient.addColorStop(1, 'rgba(16, 185, 129, 0.05)')

      this.charts.evolucao = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Lucro (R$)',
            data: values,
            borderColor: '#10b981',
            backgroundColor: gradient,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#10b981',
            pointRadius: 4,
            pointHoverRadius: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              gridLines: { color: this.getChartGridColor(), drawBorder: false },
              ticks: { fontColor: this.getChartFontColor() }
            }],
            yAxes: [{ 
              gridLines: { color: this.getChartGridColor(), drawBorder: false },
              ticks: { 
                fontColor: this.getChartFontColor(),
                beginAtZero: true, 
                callback: value => 'R$ ' + (value/1000).toFixed(0) + 'k' 
              } 
            }]
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (item) => 'Lucro: ' + this.formatBRL(item.yLabel)
            }
          }
        }
      })
    },
    renderGrupos() {
      if (!this.$refs.chartGrupos) return
      const ctx = this.$refs.chartGrupos.getContext('2d')
      if (this.charts.grupos) this.charts.grupos.destroy()
      
      const gruposMap = {}
      this.dadosLocal.forEach(it => {
        const nome = it.GRUPO || 'DIVERSOS'
        if (!gruposMap[nome]) gruposMap[nome] = 0
        gruposMap[nome] += (it.VL_LUCRO || 0)
      })
      const sorted = Object.entries(gruposMap).sort((a, b) => b[1] - a[1]).slice(0, 6)
      const labels = sorted.map(g => g[0])
      const values = sorted.map(g => g[1])
      
      this.charts.grupos = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'],
            borderWidth: this.isDark ? 0 : 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 65,
          legend: { 
            position: 'right', 
            labels: { fontColor: this.getChartFontColor(), boxWidth: 12, padding: 15 } 
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const label = data.labels[item.index]
                const val = data.datasets[0].data[item.index]
                return label + ': ' + this.formatBRL(val)
              }
            }
          }
        }
      })
    },
    renderProdutos() {
      if (!this.$refs.chartProdutos) return
      const ctx = this.$refs.chartProdutos.getContext('2d')
      if (this.charts.produtos) this.charts.produtos.destroy()
      
      const prodMap = {}
      this.dadosLocal.forEach(it => {
        const nome = it.PRODUTO || 'N/I'
        if (!prodMap[nome]) prodMap[nome] = 0
        prodMap[nome] += (it.VL_LUCRO || 0)
      })
      const sorted = Object.entries(prodMap).sort((a, b) => b[1] - a[1]).slice(0, 10)
      const labels = sorted.map(g => g[0].substring(0, 20))
      const values = sorted.map(g => g[1])
      
      this.charts.produtos = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels,
          datasets: [{
            label: 'Lucro (R$)',
            data: values,
            backgroundColor: '#3b82f6',
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              gridLines: { color: this.getChartGridColor(), drawBorder: false },
              ticks: { 
                fontColor: this.getChartFontColor(),
                beginAtZero: true,
                callback: value => 'R$ ' + (value/1000).toFixed(0) + 'k' 
              } 
            }],
            yAxes: [{ 
              gridLines: { display: false },
              ticks: { fontColor: this.getChartFontColor() } 
            }]
          },
          tooltips: {
            callbacks: {
              label: (item) => 'Lucro: ' + this.formatBRL(item.xLabel)
            }
          }
        }
      })
    }
  },
  mounted() {
    moment.locale('pt-br')
    this.buscarDados()
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(c => c && c.destroy())
  }
}
</script>

<style scoped>
.report-header-premium {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #fff;
}
.report-header-premium__icon {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.1);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem;
}
.report-header-premium__title { margin: 0; font-weight: 800; font-size: 1.4rem; letter-spacing: -0.5px; }
.report-header-premium__sub { font-size: 0.9rem; }

.bg-white-10 { background-color: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); }
.v-divider { width: 1px; height: 20px; background-color: rgba(255,255,255,0.3); }

/* Remove a seta padrao do select para ficar mais clean */
.select-transparent {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
.select-transparent:focus { outline: none; box-shadow: none; }
.select-transparent option { color: #333; }

.kpi-card-premium {
  background: #fff;
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
  border-left: 4px solid #e2e8f0;
  transition: transform 0.2s;
}
.kpi-card-premium:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
.border-left-primary { border-left-color: #3b82f6; }
.border-left-danger { border-left-color: #ef4444; }
.border-left-success { border-left-color: #10b981; }
.border-left-info { border-left-color: #0ea5e9; }

.kpi-card-premium small { text-transform: uppercase; font-weight: 700; color: #64748b; font-size: 0.7rem; display: block; margin-bottom: 0.5rem; letter-spacing: 0.5px; }
.kpi-card-premium h3 { font-weight: 800; font-size: 1.5rem; margin: 0; letter-spacing: -0.5px; }
.kpi-icon-bg { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
.bg-primary-light { background: #dbeafe; color: #3b82f6; }
.bg-danger-light { background: #fee2e2; color: #ef4444; }
.bg-success-light { background: #d1fae5; color: #10b981; }
.bg-info-light { background: #e0f2fe; color: #0ea5e9; }

.chart-card { border-radius: 12px; }

/* Adaptação Dark Mode */
.theme-dark .kpi-card-premium {
  background: #1e293b;
  border-left-color: #334155;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
}
.theme-dark .kpi-card-premium small { color: #94a3b8; }
.theme-dark .border-left-primary { border-left-color: #3b82f6; }
.theme-dark .border-left-danger { border-left-color: #ef4444; }
.theme-dark .border-left-success { border-left-color: #10b981; }
.theme-dark .border-left-info { border-left-color: #0ea5e9; }

.theme-dark .bg-primary-light { background: rgba(59, 130, 246, 0.2); }
.theme-dark .bg-danger-light { background: rgba(239, 68, 68, 0.2); }
.theme-dark .bg-success-light { background: rgba(16, 185, 129, 0.2); }
.theme-dark .bg-info-light { background: rgba(14, 165, 233, 0.2); }

@media print {
  .analytics-dashboard {
    background-color: white !important;
  }
  .report-header-premium, .filter-pills, .btn-imprimir, .btn, .pagination {
    display: none !important;
  }
  .chart-card {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #e2e8f0 !important;
  }
  .kpi-card-premium {
    box-shadow: none !important;
    border: 1px solid #e2e8f0 !important;
    border-left-width: 4px !important;
  }
  .app-header, .sidebar, .app-footer {
    display: none !important;
  }
  .main {
    margin-left: 0 !important;
    padding: 0 !important;
    background-color: white !important;
  }
  canvas {
    max-width: 100% !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/estrategia/AnalyticsDashboard.vue